import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, FileText } from "lucide-react";
import { openFile } from "@/lib/fileViewer";
import { useAuth, isStudent } from "@/lib/auth";

export const MED_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif";

export function RequestAttachments({ req }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const items = req.attachments || [];
  const canManage = !isStudent(user);

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/requests/${req.id}/attachments`, fd);
      toast.success("Allegato caricato");
      qc.invalidateQueries({ queryKey: ["request", req.id] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setBusy(false); }
  }

  async function onDelete(a) {
    if (!window.confirm(`Eliminare "${a.original_filename}"?`)) return;
    try {
      await api.delete(`/requests/attachments/${a.id}`);
      qc.invalidateQueries({ queryKey: ["request", req.id] });
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  }

  const open = (a) => openFile(`${process.env.REACT_APP_BACKEND_URL}/api/requests/attachments/${a.id}/download?auth=${encodeURIComponent(localStorage.getItem("token"))}`, a.original_filename);

  return (
    <div className="mt-5 border-t border-stone-100 pt-4" data-testid="request-attachments">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><Paperclip size={11}/> Ricette / certificati · {items.length}</div>
        <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white px-3 h-8 text-[11px] transition-colors duration-150">
          <Upload size={12}/> {busy ? "Caricamento…" : "Aggiungi"}
          <input type="file" accept={MED_ACCEPT} className="hidden" onChange={onUpload} disabled={busy} data-testid="request-attach-input"/>
        </label>
      </div>
      {items.length === 0 ? (
        <div className="text-[12px] text-stone-400">Nessun allegato. Puoi aggiungerlo anche dopo la visita.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-md px-2 py-1.5 text-[12px]">
              <button onClick={() => open(a)} className="flex-1 min-w-0 flex items-center gap-1.5 text-left hover:underline" data-testid={`request-att-open-${a.id}`}>
                <FileText size={13} className="text-stone-500 shrink-0"/><span className="truncate">{a.original_filename}</span>
              </button>
              {(canManage || a.uploaded_by === user?.id) && (
                <button onClick={() => onDelete(a)} className="text-stone-400 hover:text-red-600 p-0.5" data-testid={`request-att-del-${a.id}`}><Trash2 size={13}/></button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] text-stone-400 mt-1.5">Visibili solo allo studente e alla segreteria/direzione.</div>
    </div>
  );
}
